import React from 'react';
import omit from 'lodash/omit';
import update from 'lodash/fp/update';
import { FormControlLabel, Switch, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { useApolloClient } from '@apollo/client';
import { Link } from 'react-router-dom';
import { allColumns, baseColumns, statusColumn } from './columns';
import HospitalMobileListItem from '../hospitals/HospitalMobileListItem';
import DoctorOfficeInput from '../../inputs/surgeonOffices/DoctorOfficeInput';
import scheme, { restoreSurgeonOffice } from '../../../graph/surgeonOffices';
import PWAInstallBanner from '@/se/components/PWAInstallBanner';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import { withSession } from '../../../state/Session';
import useDebugMode from '../../../hooks/useDebugMode';
import { flow } from 'lodash';

const Empty = { illustration: theme => theme.illustrations.rooms };

const ReadOnlyEmpty = {
  illustration: theme => theme.illustrations.rooms,
  title: 'No organizations assigned.',
  hint: 'Contact a SuperAdmin to be assigned to organizations.',
};

const VoidedFilter = ({ value, onChange }) => {
  const includeVoided = value?.includeVoided || false;
  return (
    <Box display="flex" alignItems="center" py={0.5}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={includeVoided}
            onChange={e => onChange({ ...value, includeVoided: e.target.checked })}
            color="primary"
          />
        }
        label={<Typography variant="body2">Show voided</Typography>}
      />
    </Box>
  );
};

// Base list config for read-only roles (Monitoring, GroupAdmin)
export const List = {
  columns: [...baseColumns],
  tableKey: 'DoctorOffices',
  Empty: ReadOnlyEmpty,
  MobileItemComponent: HospitalMobileListItem,
};

// SuperAdmin list config — includes voided toggle, status column, and row styling
export const SuperAdminList = {
  ...List,
  Empty,
  columns: [...baseColumns],
  FilterComponent: VoidedFilter,
  highlightedRowStyleProvider: item => (item.voidedAt ? { opacity: 0.5 } : undefined),
  queryOptionsProvider: props => ({
    variables: {
      includeVoided: props.filter?.includeVoided || false,
    },
    fetchPolicy: 'cache-and-network',
  }),
  mapListProps: props => ({
    columns: props.filter?.includeVoided ? [...baseColumns, statusColumn] : [...baseColumns],
  }),
};

const setDefaultSessionTimeout = update('sessionTimeout', sessionTimeout => sessionTimeout || 10 * 60);
const setDefaultQuestionnaireValidMonths = update(
  'questionnaireValidMonths',
  questionnaireValidMonths => questionnaireValidMonths || 6
);
const updateTimezone = update('timezone', timezone => (timezone && timezone.id ? timezone.id : timezone));

export const Create = {
  Input: DoctorOfficeInput,
  Empty,
  initialValue: {
    questionnaireValidMonths: 6,
    sessionTimeout: 600,
  },
  prepareCreateData: flow(setDefaultQuestionnaireValidMonths, setDefaultSessionTimeout, updateTimezone),
};

export const ShowActions = ({ data = {} }) => {
  const client = useApolloClient();
  const { isSuperAdmin } = useDebugMode();
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const handleRestore = async () => {
    if (!data.id) return;
    try {
      setBusy(true);
      setMessage(null);
      await client.mutate({
        mutation: restoreSurgeonOffice,
        variables: { id: parseInt(data.id, 10) },
        refetchQueries: ['surgeonOffices', 'surgeonOffice'],
      });
      setMessage('Organization restored.');
    } catch (error) {
      console.error('Failed to restore organization', error);
      setMessage(error?.message || 'Failed to restore organization.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gridGap="0.5rem">
      {message && (
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      )}
      {isSuperAdmin && data.voidedAt && (
        <Button
          variant="outlined"
          onClick={handleRestore}
          disabled={busy}
          style={{ color: '#4caf50', borderColor: '#4caf50' }}
        >
          {busy ? 'Restoring…' : 'Restore Organization'}
        </Button>
      )}
      <Button variant="contained" component={Link} to={`/su/${data.id}`}>
        Manage
      </Button>
    </Box>
  );
};

export const Show = {
  columns: allColumns,
  Empty,
  Actions: ShowActions,
};

export const Edit = {
  Input: DoctorOfficeInput,
  Empty,
  prepareUpdateData: flow(setDefaultQuestionnaireValidMonths, setDefaultSessionTimeout, updateTimezone),
};

const CRUD = GraphQLCRUD({
  entityName: 'SurgeonOffice',
  scheme,
  List: SuperAdminList,
  Create,
  Show,
  Edit,
});

const RU = GraphQLCRUD({
  entityName: 'SurgeonOffice',
  scheme: omit(scheme, 'remove'),
  List,
  Show,
});

const DoctorOffices = ({ isGroupAdmin }) => (
  <React.Fragment>
    <PWAInstallBanner />
    {isGroupAdmin ? <RU /> : <CRUD />}
  </React.Fragment>
);

export default withSession(unpackSessionObject)(DoctorOffices);
